import { useRef, useState, useEffect } from 'react';
import { searchEmojiShortcuts } from '../../lib/emoji-shortcuts';
import { EmojiAutocomplete } from '../shared/EmojiAutocomplete';
import { EmojiPicker } from '../shared/EmojiPicker';
import { Button } from '../ui/Button';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  sending: boolean;
  placeholder?: string;
}

export function MessageInput({
  value,
  onChange,
  onSubmit,
  sending,
  placeholder = 'Type a message...',
}: MessageInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Emoji autocomplete state
  const [, setAutocompleteQuery] = useState<string | null>(null);
  const [autocompleteMatches, setAutocompleteMatches] = useState<
    Array<{ shortcode: string; emoji: string }>
  >([]);
  const [autocompleteIndex, setAutocompleteIndex] = useState(0);
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle emoji autocomplete navigation
    if (autocompleteMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setAutocompleteIndex((prev) => (prev + 1) % autocompleteMatches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setAutocompleteIndex(
          (prev) => (prev - 1 + autocompleteMatches.length) % autocompleteMatches.length
        );
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const selected = autocompleteMatches[autocompleteIndex];
        if (selected) {
          handleAutocompleteSelect(selected.emoji);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setAutocompleteQuery(null);
        setAutocompleteMatches([]);
        return;
      }
    }

    // Regular enter to send message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const currentValue = e.currentTarget.value;
      if (!currentValue.trim()) {
        return;
      }

      const form = e.currentTarget.form;
      if (form) {
        form.requestSubmit();
      }
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    // Insert emoji at cursor position or at end
    const input = inputRef.current;
    if (!input) {
      onChange(value + emoji);
      return;
    }

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = value.substring(0, start) + emoji + value.substring(end);
    onChange(newValue);

    // Move cursor after emoji and focus input
    setTimeout(() => {
      input.focus();
      const newCursorPos = start + emoji.length;
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleAutocompleteSelect = (emoji: string) => {
    const input = inputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart || 0;

    // Find the start of the :shortcode
    const beforeCursor = value.substring(0, cursorPos);
    const match = beforeCursor.match(/:([a-z0-9_+-]*)$/);

    if (match) {
      const matchStart = cursorPos - match[0].length;
      const newValue = value.substring(0, matchStart) + emoji + ' ' + value.substring(cursorPos);
      onChange(newValue);

      // Clear autocomplete
      setAutocompleteQuery(null);
      setAutocompleteMatches([]);

      // Move cursor after emoji
      setTimeout(() => {
        input.focus();
        const newCursorPos = matchStart + emoji.length + 1;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  // Detect emoji shortcodes as user types
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart || 0;
    const beforeCursor = value.substring(0, cursorPos);
    const match = beforeCursor.match(/:([a-z0-9_+-]*)$/);

    if (match) {
      const query = match[1];
      setAutocompleteQuery(query);

      if (query.length > 0) {
        const matches = searchEmojiShortcuts(query, 8);
        setAutocompleteMatches(matches);
        setAutocompleteIndex(0);

        // Calculate position for autocomplete dropdown
        const inputRect = input.getBoundingClientRect();
        setAutocompletePosition({
          top: window.innerHeight - inputRect.top + 8,
          left: inputRect.left,
        });
      } else {
        setAutocompleteMatches([]);
      }
    } else {
      setAutocompleteQuery(null);
      setAutocompleteMatches([]);
    }
  }, [value]);

  return (
    <div className="p-4 bg-slate-800 border-t border-slate-700">
      <form onSubmit={onSubmit} className="flex gap-2 relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 px-4 py-3 sm:py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
          disabled={sending}
          data-testid="message-input"
        />

        {/* Emoji picker button */}
        <button
          type="button"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className="min-w-[44px] min-h-[44px] px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors active:scale-95 touch-manipulation"
          disabled={sending}
          aria-label="Open emoji picker"
          data-testid="emoji-picker-button"
        >
          😊
        </button>

        <Button
          type="submit"
          disabled={sending || !value.trim()}
          loading={sending}
          variant="primary"
          className="min-w-[44px] min-h-[44px] touch-manipulation"
        >
          {sending ? 'Sending...' : 'Send'}
        </Button>

        {/* Emoji picker */}
        <EmojiPicker
          show={showEmojiPicker}
          onSelect={handleEmojiSelect}
          onClose={() => setShowEmojiPicker(false)}
        />

        {/* Emoji autocomplete */}
        {autocompleteMatches.length > 0 && (
          <EmojiAutocomplete
            matches={autocompleteMatches}
            selectedIndex={autocompleteIndex}
            onSelect={handleAutocompleteSelect}
            position={autocompletePosition}
          />
        )}
      </form>
    </div>
  );
}

// Export the ref type for parent components
export { type MessageInputProps };
